import { createContext, useContext, useEffect, useState, Fragment, type ReactNode } from 'react';
import { Link, useMatches } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { UserText } from './UserText';
import { useClientFrame } from '../advisor/useClientFrame';

// Matches the app-wide Lucide stroke (docs/04 §1). Defined locally rather than
// imported from Layout to avoid a Layout↔Breadcrumbs import cycle.
const STROKE = 1.75;

/**
 * Breadcrumb trail for the top app bar (docs/04 §2).
 *
 * Static crumbs come from each route's `handle.crumb` (declared in `router.tsx`),
 * resolved via `useMatches()` — so the IA structure lives in one place. Detail
 * pages (account, document) append a *dynamic* leaf — the record's own name —
 * by calling `useDynamicCrumb(name)`; the value is held in context here so the
 * bar (rendered up in the Layout header) can read what a descendant page set.
 *
 * The "Portfolio" crumb links to `/portfolio`, the Portfolio view — which
 * doubles as the "back to the Portfolio home" affordance from any sub-page.
 */

interface RouteCrumbHandle {
  crumb?: string;
}

interface Crumb {
  label: string;
  to?: string;
  /** Present instead of `to` for a crumb that performs an ACTION rather than a plain
   *  navigation — the client frame's leading "Clients" crumb (steward-app#1401), which
   *  must switch the active workspace back to the firm before landing on `/advisor`
   *  (`useClientFrame`'s `exitToClients`), not just follow a link into it. */
  onClick?: () => void;
  /** True for the dynamic leaf, which is a record's own name and so is text somebody
   *  typed. The static crumbs come from `router.tsx` and are ours (steward-app#600). */
  userText?: boolean;
}

/** A crumb a detail page inserts between the route's static crumbs and its own leaf. */
export interface ParentCrumb {
  label: string;
  to: string;
}

const BreadcrumbContext = createContext<{
  dynamic: string | null;
  setDynamic: (label: string | null) => void;
  parents: readonly ParentCrumb[];
  setParents: (parents: readonly ParentCrumb[]) => void;
}>({ dynamic: null, setDynamic: () => {}, parents: [], setParents: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [dynamic, setDynamic] = useState<string | null>(null);
  const [parents, setParents] = useState<readonly ParentCrumb[]>([]);
  return (
    <BreadcrumbContext.Provider value={{ dynamic, setDynamic, parents, setParents }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/** Read the current dynamic (detail-record) crumb — used by the document title. */
export function useDynamicCrumbValue() {
  return useContext(BreadcrumbContext).dynamic;
}

/**
 * Append the current record's name as the trailing crumb (e.g. an account or
 * document title). Cleared automatically on unmount / navigation away.
 *
 * `parent` (crossmarc/steward-app#1763) is one linked crumb placed before the leaf,
 * for a record whose route cannot say where it belongs: `/advisor/clients/:clientId`
 * serves a prospect and a client alike, so the "Prospects" step in RIPL's
 * "Clients / Prospects / Name" trail is known only once the record has loaded.
 * A single parent, keyed by its label and path, so the effect below depends on
 * primitives rather than on an array a caller rebuilds every render.
 */
export function useDynamicCrumb(label: string | null | undefined, parent?: ParentCrumb | null) {
  const { setDynamic, setParents } = useContext(BreadcrumbContext);
  const parentLabel = parent?.label;
  const parentTo = parent?.to;
  useEffect(() => {
    setDynamic(label ?? null);
    setParents(parentLabel && parentTo ? [{ label: parentLabel, to: parentTo }] : []);
    return () => { setDynamic(null); setParents([]); };
  }, [setDynamic, setParents, label, parentLabel, parentTo]);
}

export function Breadcrumbs() {
  const matches = useMatches();
  const { dynamic, parents } = useContext(BreadcrumbContext);
  // Same hook `SidebarContent`, `ClientFrameBand` and `ViewOnlyPill` each call
  // independently (steward-app#1401, `useClientFrame`'s own header) — nothing here is
  // threaded from a shared parent, so none of the four can disagree about the state.
  const { inClientFrame, clientName, exitToClients } = useClientFrame();

  const crumbs: Crumb[] = matches
    .filter(m => (m.handle as RouteCrumbHandle | undefined)?.crumb)
    .map(m => ({ label: (m.handle as RouteCrumbHandle).crumb!, to: m.pathname }));

  // The client frame's own leading crumbs (steward-app#1401): "Clients" is one of the
  // three equivalent exits (the band, the nav item, this crumb — browser back is a
  // CORRECTION rather than a fourth switch, see `useClientFrame`'s own doc), so it
  // performs the same switch-and-navigate as the others rather than a plain link into
  // `/advisor/clients` that would leave the client workspace active. The client's own
  // name follows as an ordinary link to its home —
  // unlike "Clients" it needs no action, the workspace is already the active one.
  // Omitted (not shown empty) when the name hasn't loaded, same rule the sidebar label
  // and the band follow. `inClientFrame`, not the broader `inClientWorkspace`
  // (`useClientFrame`'s header): a firm's own staff member in their OWN family
  // workspace gets the ordinary route-derived trail, not a false "Clients" prefix.
  if (inClientFrame) {
    crumbs.unshift(
      { label: 'Clients', onClick: exitToClients },
      ...(clientName ? [{ label: clientName, to: '/', userText: true }] : []),
    );
  }

  // A parent only ever precedes a leaf: with no leaf it would itself render as the
  // current page, claiming a location the page never set.
  if (dynamic) crumbs.push(...parents, { label: dynamic, userText: true });
  if (crumbs.length === 0) return null;

  // The leaf is the current page — render it as plain text, never a link.
  const lastIndex = crumbs.length - 1;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-1.5 font-sans text-[12px]"
    >
      {crumbs.map((c, i) => {
        const isLast = i === lastIndex;
        return (
          <Fragment key={`${c.label}-${i}`}>
            {i > 0 && (
              <ChevronRight size={13} strokeWidth={STROKE} aria-hidden className="hidden sm:flex shrink-0 text-slate/50" />
            )}
            {isLast || (!c.to && !c.onClick) ? (
              <span aria-current="page" className="truncate font-semibold text-navy">
                {c.userText ? <UserText>{c.label}</UserText> : c.label}
              </span>
            ) : c.onClick ? (
              <button
                type="button"
                onClick={c.onClick}
                className="hidden sm:flex shrink-0 items-center border-none bg-transparent p-0 font-sans text-[12px] text-slate no-underline cursor-pointer transition-colors duration-150 hover:text-navy"
              >
                {c.userText ? <UserText>{c.label}</UserText> : c.label}
              </button>
            ) : (
              <Link
                to={c.to!}
                className="hidden sm:flex shrink-0 text-slate no-underline transition-colors duration-150 hover:text-navy"
              >
                {c.userText ? <UserText>{c.label}</UserText> : c.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
